import { useState, useEffect } from 'react';
import { useParams, Link } from 'react-router-dom';
import { Calendar, Eye, ArrowLeft, ArrowRight, Tag, User, Share2, Newspaper } from 'lucide-react';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatDate = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  return d.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
};

const renderContent = (content) => {
  if (!content) return null;
  // Simple markdown-like rendering
  return content.split('\n\n').map((block, i) => {
    if (block.startsWith('### ')) {
      return <h3 key={i} className="text-xl font-bold text-[#1F2937] mt-8 mb-3">{block.slice(4)}</h3>;
    }
    if (block.startsWith('## ')) {
      return <h2 key={i} className="text-2xl font-bold text-[#1F2937] mt-10 mb-4">{block.slice(3)}</h2>;
    }
    if (block.startsWith('# ')) {
      return <h1 key={i} className="text-3xl font-bold text-[#1F2937] mt-10 mb-4">{block.slice(2)}</h1>;
    }
    if (block.startsWith('- ') || block.startsWith('* ')) {
      const items = block.split('\n').filter(l => l.startsWith('- ') || l.startsWith('* '));
      return (
        <ul key={i} className="list-disc pl-6 space-y-1.5 my-4 text-gray-600">
          {items.map((item, j) => <li key={j}>{item.slice(2)}</li>)}
        </ul>
      );
    }
    if (block.startsWith('> ')) {
      return (
        <blockquote key={i} className="border-l-4 border-[#0D9488] pl-4 italic text-gray-500 my-6">
          {block.slice(2)}
        </blockquote>
      );
    }
    // Bold and italic inline
    let html = block
      .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
      .replace(/\*(.+?)\*/g, '<em>$1</em>');
    return <p key={i} className="text-gray-600 leading-relaxed my-3" dangerouslySetInnerHTML={{ __html: html }} />;
  });
};

export const BlogArticlePage = () => {
  const { slug } = useParams();
  const [article, setArticle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      setLoading(true);
      try {
        const { data } = await axios.get(`${API}/blog/articles/${slug}`);
        setArticle(data);
      } catch {
        setArticle(null);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [slug]);

  const handleShare = () => {
    navigator.clipboard.writeText(window.location.href);
    toast.success('Lien copié dans le presse-papiers !');
  };

  if (loading) {
    return (
      <div className="bg-[#F8FAFC] min-h-[60vh]">
        <div className="max-w-4xl mx-auto px-4 py-12 animate-pulse space-y-6">
          <div className="h-4 bg-gray-200 rounded w-32" />
          <div className="h-10 bg-gray-200 rounded w-3/4" />
          <div className="h-64 bg-gray-200 rounded-xl" />
          <div className="space-y-3">
            <div className="h-4 bg-gray-200 rounded w-full" />
            <div className="h-4 bg-gray-200 rounded w-5/6" />
            <div className="h-4 bg-gray-200 rounded w-4/6" />
          </div>
        </div>
      </div>
    );
  }

  if (!article) {
    return (
      <div className="bg-[#F8FAFC] min-h-[60vh] flex items-center justify-center">
        <div className="text-center">
          <Newspaper className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h2 className="text-xl font-bold text-gray-500 mb-2">Article introuvable</h2>
          <Link to="/blog" className="text-[#0D9488] font-medium hover:underline">
            Retour au blog
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-[#F8FAFC] min-h-[60vh]" data-testid="blog-article-page">
      <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {/* Back link */}
        <Link
          to="/blog"
          className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-[#0D9488] mb-6 transition-colors"
          data-testid="back-to-blog"
        >
          <ArrowLeft className="w-4 h-4" />
          Retour au blog
        </Link>

        {/* Category + Meta */}
        <div className="flex flex-wrap items-center gap-3 mb-4">
          <span className="bg-[#0D9488] text-white text-xs font-semibold px-2.5 py-1 rounded-md">
            {article.category}
          </span>
          <span className="flex items-center gap-1 text-xs text-gray-400">
            <Calendar className="w-3 h-3" />
            {formatDate(article.created_at)}
          </span>
          <span className="flex items-center gap-1 text-xs text-gray-400">
            <Eye className="w-3 h-3" />
            {article.views || 0} lectures
          </span>
        </div>

        {/* Title */}
        <h1 className="text-3xl sm:text-4xl font-bold text-[#1F2937] mb-4" data-testid="article-title">
          {article.title}
        </h1>

        {/* Excerpt */}
        {article.excerpt && (
          <p className="text-lg text-gray-500 mb-6">{article.excerpt}</p>
        )}

        {/* Author + Share */}
        <div className="flex items-center justify-between mb-8 pb-6 border-b border-gray-200">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-[#0D9488] rounded-full flex items-center justify-center">
              <User className="w-5 h-5 text-white" />
            </div>
            <div>
              <p className="text-sm font-semibold text-[#1F2937]">{article.author?.name || 'AERH'}</p>
              <p className="text-xs text-gray-400">{article.author?.role || 'Equipe AERH'}</p>
            </div>
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={handleShare}
            className="text-gray-500 border-gray-200"
            data-testid="share-button"
          >
            <Share2 className="w-4 h-4 mr-1.5" />
            Partager
          </Button>
        </div>

        {/* Cover Image */}
        {article.cover_image && (
          <div className="rounded-xl overflow-hidden mb-8 shadow-md">
            <img src={article.cover_image} alt={article.title} className="w-full h-auto max-h-[480px] object-cover" />
          </div>
        )}

        {/* Content */}
        <div className="prose-container" data-testid="article-content">
          {renderContent(article.content)}
        </div>

        {/* Tags */}
        {article.tags?.length > 0 && (
          <div className="flex flex-wrap gap-2 mt-10 pt-6 border-t border-gray-200" data-testid="article-tags">
            <Tag className="w-4 h-4 text-gray-400" />
            {article.tags.map((tag) => (
              <span key={tag} className="text-sm text-teal-600 bg-teal-50 px-3 py-1 rounded-full">{tag}</span>
            ))}
          </div>
        )}

        {/* Related Articles */}
        {article.related?.length > 0 && (
          <div className="mt-12 pt-8 border-t border-gray-200" data-testid="related-articles">
            <h3 className="text-xl font-bold text-[#1F2937] mb-6">Articles similaires</h3>
            <div className="grid sm:grid-cols-3 gap-6">
              {article.related.map((rel) => (
                <Link
                  key={rel.id}
                  to={`/blog/${rel.slug}`}
                  className="group bg-white rounded-xl border border-gray-200 overflow-hidden hover:shadow-md transition-all"
                >
                  {rel.cover_image ? (
                    <img src={rel.cover_image} alt={rel.title} className="w-full h-32 object-cover" />
                  ) : (
                    <div className="w-full h-32 bg-gradient-to-br from-teal-500 to-teal-700" />
                  )}
                  <div className="p-3">
                    <p className="text-xs text-gray-400 mb-1">{formatDate(rel.created_at)}</p>
                    <h4 className="text-sm font-semibold text-[#1F2937] group-hover:text-[#0D9488] line-clamp-2">
                      {rel.title}
                    </h4>
                  </div>
                </Link>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default BlogArticlePage;
